// src/utils/installPlatform.js
// Works out which device/browser the visitor is on and keeps hold of the browser's native
// "install app" prompt (Chrome / Edge / Samsung Internet fire `beforeinstallprompt`; Safari and
// Firefox never do, so those need written steps).

const ua = () => (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');

export function detectPlatform() {
  const agent = ua();
  const touch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;

  // iPadOS 13+ reports itself as a Mac, so tell it apart by touch support
  const isIos = /iPad|iPhone|iPod/.test(agent) || (/Macintosh/.test(agent) && touch);
  const isAndroid = /Android/.test(agent);
  const os = isIos ? 'ios' : isAndroid ? 'android' : /Macintosh/.test(agent) ? 'mac' : /Windows/.test(agent) ? 'windows' : 'other';

  let browser = 'other';
  if (/EdgA|EdgiOS|Edg\//.test(agent)) browser = 'edge';
  else if (/SamsungBrowser/.test(agent)) browser = 'samsung';
  else if (/OPR|OPiOS|Opera/.test(agent)) browser = 'opera';
  else if (/FxiOS|Firefox/.test(agent)) browser = 'firefox';
  else if (/CriOS|Chrome|Chromium/.test(agent)) browser = 'chrome';
  else if (/Safari/.test(agent)) browser = 'safari';

  const safariVersion = Number((agent.match(/Version\/(\d+)/) || [])[1]) || 0;
  // Instagram / Facebook / TikTok etc. open links in a webview with no install or share-to-home-screen
  const inApp = /FBAN|FBAV|Instagram|Line\/|TikTok|musical_ly|Snapchat|Twitter|LinkedInApp|; wv\)/i.test(agent);

  return { os, browser, safariVersion, inApp, isMobile: os === 'ios' || os === 'android' };
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
}

// Which written guide applies to this visitor. Chromium browsers return 'chromium-*' and normally
// get the one-tap native prompt instead; the steps are the fallback.
export function guideIdFor(p = detectPlatform()) {
  if (p.os === 'ios') return 'ios';
  if (p.os === 'android') return p.browser === 'firefox' ? 'android-firefox' : 'android-chromium';
  if (p.browser === 'safari') return p.safariVersion >= 17 ? 'mac-safari' : 'desktop-other';
  if (p.browser === 'firefox') return 'desktop-firefox';
  if (p.browser === 'chrome' || p.browser === 'edge' || p.browser === 'opera') return 'desktop-chromium';
  return 'desktop-other';
}

// Browsers that can be installed but never fire the native prompt: we can only show steps
export function needsManualSteps(p = detectPlatform()) {
  const id = guideIdFor(p);
  return !p.inApp && ['ios', 'mac-safari', 'android-firefox'].includes(id);
}

// ── Native install prompt (Chromium) ────────────────────────────────────────────────────────
let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  // Registered at import time so the (one-shot) event isn't missed before React has mounted
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export const canPromptInstall = () => !!deferredPrompt;
export const subscribeInstall = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// Resolves to 'accepted' | 'dismissed' | 'unavailable'
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  const evt = deferredPrompt;
  deferredPrompt = null; // an event can only be used once
  notify();
  try {
    await evt.prompt();
    const choice = await evt.userChoice;
    return choice.outcome;
  } catch (e) {
    return 'dismissed';
  }
}

// Where to switch notifications back on after the visitor blocked them
export function blockedNotificationHelp(p = detectPlatform()) {
  if (p.os === 'ios') return 'Open the iPhone Settings app → Notifications → Greek Ranking → turn on Allow Notifications.';
  if (p.os === 'android') return 'Tap the lock (or ⓘ) icon next to the address bar → Permissions / Notifications → Allow. Or long-press the app icon → App info → Notifications.';
  if (p.browser === 'safari') return 'Safari menu → Settings → Websites → Notifications → set greek-ranking.web.app to Allow.';
  if (p.browser === 'firefox') return 'Click the lock icon next to the address bar → Permissions → Receive Notifications → clear the block, then reload.';
  return 'Click the lock (or tune) icon next to the address bar → Notifications → Allow, then reload the page.';
}
